import { Link } from 'wouter';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ChevronRight, User } from 'lucide-react';

interface CustomerAccessProps {
  /** Applied to the grid cell wrapper so the caller can set column order. */
  className?: string;
}

/**
 * Customer half of the login page. Customers have no account, so this is a
 * signpost to /my-bookings — which asks for their email and mails back a link.
 *
 * Two presentations, one destination:
 *   - below lg, a compact strip. It sits ABOVE the business form so the
 *     customer route is visible without scrolling — customers who land on
 *     /login are the ones who lost their email link, and are the least able
 *     to recover from a hidden affordance. The strip costs the business form
 *     ~76px, which keeps email, password and submit above the fold.
 *   - at lg and up, a full card in the narrower right-hand column, where
 *     both routes are visible at once and no ordering question arises.
 *
 * This used to open the magic link form in a dialog. It navigates instead,
 * because a modal was the wrong container on a phone: Radix moves focus to the
 * first input on open, iOS raises the keyboard and scrolls the visual viewport
 * to it, and the scroll lock leaves nothing to pan back — the dialog reads as
 * frozen. A page has no focus trap, no scroll lock and no fixed panel fighting
 * the keyboard, and it gives the step a real URL and a working back button.
 * /my-bookings already carries the same form with a heading, the reassurance
 * points and the shared chrome, so nothing is duplicated here.
 */
export function CustomerAccess({ className }: CustomerAccessProps) {
  const { t } = useTranslation('login');

  return (
    <div className={className}>
      {/* Mobile: compact strip, above the form. */}
      <Link
        href="/my-bookings"
        className="flex w-full min-h-touch-target items-center gap-3 rounded-2xl border border-brand-200 bg-brand-50 p-4 text-left transition-colors hover:bg-brand-100 lg:hidden"
      >
        <span className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-lg border border-brand-200 bg-surface text-brand-500">
          <User className="h-[17px] w-[17px]" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-grow">
          <span className="block text-sm font-semibold text-ink">
            {t('chooser.customer.title')}
          </span>
          <span className="block text-body-xs text-brand-700">
            {t('chooser.customer.strip')}
          </span>
        </span>
        <ChevronRight className="h-[18px] w-[18px] flex-shrink-0 text-brand-500" aria-hidden="true" />
      </Link>

      {/* Desktop: full card in the narrow column. */}
      <div className="hidden rounded-2xl border border-brand-200 bg-brand-50 p-[22px] lg:block">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-brand-200 bg-surface text-brand-500">
            <User className="h-[19px] w-[19px]" aria-hidden="true" />
          </span>
          <h2 className="text-h3 text-ink">{t('chooser.customer.title')}</h2>
        </div>

        <p className="mt-3.5 text-body-sm text-brand-700">
          {t('chooser.customer.description')}
        </p>

        <Link
          href="/my-bookings"
          className="mt-3.5 flex h-12 w-full items-center justify-center gap-2 rounded-lg border border-brand-500 bg-surface px-5 text-[14.5px] font-medium text-brand-500 transition-colors hover:bg-brand-50"
        >
          {t('chooser.customer.cta')}
          <ArrowRight className="h-[17px] w-[17px]" aria-hidden="true" />
        </Link>

        <p className="mt-3.5 text-body-xs text-soft">{t('chooser.customer.note')}</p>
      </div>
    </div>
  );
}
